import { API_BASE_URL } from "../../../../config/api";
import { getToken } from "../../../../shared/data/storage/authStorage";
import {
  BillExportRequestDTO,
  BillExportResponseDTO,
} from "../models/BillExportDTOs";

class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

const parseFilename = (contentDisposition: string | null, fallback: string) => {
  if (!contentDisposition) return fallback;
  const match = contentDisposition.match(/filename="?(.+?)"?(\s*;|$)/i);
  return match?.[1] || fallback;
};

export const fetchBillExportExcel = async (
  params: BillExportRequestDTO = {},
): Promise<BillExportResponseDTO> => {
  const token = getToken();
  if (!token) {
    throw new APIError("No authentication token found", 401);
  }

  const queryParams = new URLSearchParams();
  if (params.from_date) queryParams.append("from_date", params.from_date);
  if (params.to_date) queryParams.append("to_date", params.to_date);

  const qs = queryParams.toString();
  const url = `${API_BASE_URL}/api/exports/bills/excel${qs ? `?${qs}` : ""}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new APIError(
      errorData.message || "Failed to download bills export",
      response.status,
      errorData,
    );
  }

  const contentType = response.headers.get("Content-Type") || "";
  if (contentType.includes("application/json")) {
    const data = await response.json().catch(() => ({}));
    throw new APIError(
      data.message || "No records found for the selected date range.",
      response.status,
      data,
    );
  }

  const suffix =
    params.from_date && params.to_date
      ? `${params.from_date}_to_${params.to_date}`
      : new Date().toISOString().split("T")[0];
  const fallbackName = `bills_export_${suffix}.xlsx`;

  const filename = parseFilename(
    response.headers.get("Content-Disposition"),
    fallbackName,
  );
  const blob = await response.blob();
  return { blob, filename };
};

